import { supabase } from "@/integrations/supabase/client";

export class SessionManager {
  private static instance: SessionManager;
  private sessionId: string | null = null;
  private visitorId: string | null = null;
  private sessionStartTime: Date | null = null;
  private lastActivityTime: Date = new Date();
  private currentPageViewId: string | null = null;
  private activityCheckInterval: number | null = null;
  private isAdminSession: boolean = false;

  private constructor() {
    // Check if this is an admin session
    this.isAdminSession = this.checkIfAdminSession();
    
    // Only init session for non-admin sessions
    if (!this.isAdminSession) {
      this.initSession();
      this.setupActivityTracking();
      this.setupBeforeUnload();
    }
  }

  // Check if this browser has accessed admin
  private checkIfAdminSession(): boolean {
    // Check localStorage for admin access flag
    const hasAccessedAdmin = localStorage.getItem('is_admin_session') === 'true';
    return hasAccessedAdmin;
  }

  // Mark this session as admin session (called when accessing admin pages)
  static markAsAdminSession() {
    localStorage.setItem('is_admin_session', 'true');
  }

  // Check if current page is admin page
  static isAdminPage(path: string): boolean {
    return path.startsWith('/admin');
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  private generateVisitorId(): string {
    const stored = localStorage.getItem('visitor_id');
    if (stored) return stored;
    
    const newId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitor_id', newId);
    return newId;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initSession() {
    this.visitorId = this.generateVisitorId();
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date();

    const deviceInfo = this.getDeviceInfo();
    const utmParams = this.getUTMParams();

    try {
      await supabase.from('user_sessions').insert({
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        device_type: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        screen_resolution: deviceInfo.screenResolution,
        user_agent: navigator.userAgent,
        landing_page: window.location.pathname,
        started_at: this.sessionStartTime.toISOString(),
        last_activity_at: this.sessionStartTime.toISOString(),
        ...utmParams,
        referrer: document.referrer || null
      });
    } catch (error) {
      console.error('Error creating session:', error);
    }
  }

  private getDeviceInfo() {
    const ua = navigator.userAgent;
    const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
    
    let deviceType = 'desktop';
    if (isTablet) deviceType = 'tablet';
    else if (isMobile) deviceType = 'mobile';

    let browser = 'Unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return {
      deviceType,
      browser,
      os,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    };
  }

  private getUTMParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content')
    };
  }

  private setupActivityTracking() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.lastActivityTime = new Date();
      });
    });

    this.activityCheckInterval = window.setInterval(() => {
      this.updateLastActivity();
    }, 30000); // Every 30 seconds
  }

  private async updateLastActivity() {
    if (!this.sessionId) return;

    try {
      await supabase
        .from('user_sessions')
        .update({ last_activity_at: new Date().toISOString() })
        .eq('session_id', this.sessionId);
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  }

  private setupBeforeUnload() {
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });
  }

  async trackPageView(pageType: string, pageUrl: string, pageTitle: string, landingPageId?: string, productId?: string) {
    // Skip tracking for admin pages or admin sessions
    if (!this.sessionId || this.isAdminSession || SessionManager.isAdminPage(pageUrl)) return null;

    const previousPageUrl = this.currentPageViewId ? pageUrl : null;

    try {
      // End previous page view
      if (this.currentPageViewId) {
        await this.endPageView(this.currentPageViewId);
      }

      // Create new page view
      const { data, error } = await supabase
        .from('page_views')
        .insert({
          session_id: (await this.getSessionDatabaseId())!,
          landing_page_id: landingPageId || null,
          product_id: productId || null,
          page_type: pageType,
          page_url: pageUrl,
          page_title: pageTitle,
          previous_page_url: previousPageUrl,
          entry_time: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      this.currentPageViewId = data.id;
      return data.id;
    } catch (error) {
      console.error('Error tracking page view:', error);
      return null;
    }
  }

  async endPageView(pageViewId: string) {
    try {
      await supabase
        .from('page_views')
        .update({
          exit_time: new Date().toISOString()
        })
        .eq('id', pageViewId);
    } catch (error) {
      console.error('Error ending page view:', error);
    }
  }

  async trackInteraction(
    interactionType: string,
    elementType?: string,
    elementId?: string,
    elementClass?: string,
    elementText?: string,
    x?: number,
    y?: number,
    metadata?: Record<string, any>
  ) {
    // Skip tracking for admin sessions
    if (!this.sessionId || !this.currentPageViewId || this.isAdminSession) return;

    const sessionDbId = await this.getSessionDatabaseId();
    if (!sessionDbId) return;

    try {
      await supabase.from('user_interactions').insert({
        session_id: sessionDbId,
        page_view_id: this.currentPageViewId,
        interaction_type: interactionType,
        element_type: elementType,
        element_id: elementId,
        element_class: elementClass,
        element_text: elementText,
        x_position: x,
        y_position: y,
        scroll_depth: window.scrollY,
        metadata: metadata || {}
      });
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  }

  async trackHeatmap(
    pageType: string,
    pageUrl: string,
    x: number,
    y: number,
    interactionType: string,
    elementSelector?: string,
    landingPageId?: string,
    productId?: string
  ) {
    // Skip tracking for admin pages or admin sessions
    if (this.isAdminSession || SessionManager.isAdminPage(pageUrl)) return;
    
    try {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Check if entry exists for today
      const today = new Date().toISOString().split('T')[0];
      
      const { data: existing } = await supabase
        .from('heatmap_data')
        .select('id, click_count')
        .eq('page_url', pageUrl)
        .eq('x_position', x)
        .eq('y_position', y)
        .eq('viewport_width', viewportWidth)
        .eq('viewport_height', viewportHeight)
        .eq('date', today)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('heatmap_data')
          .update({ click_count: existing.click_count + 1 })
          .eq('id', existing.id);
      } else {
        await supabase.from('heatmap_data').insert({
          landing_page_id: landingPageId || null,
          product_id: productId || null,
          page_type: pageType,
          page_url: pageUrl,
          x_position: x,
          y_position: y,
          viewport_width: viewportWidth,
          viewport_height: viewportHeight,
          click_count: 1,
          interaction_type: interactionType,
          element_selector: elementSelector,
          date: today
        });
      }
    } catch (error) {
      console.error('Error tracking heatmap:', error);
    }
  }

  async updateScrollDepth(percentage: number) {
    if (!this.currentPageViewId) return;

    try {
      await supabase
        .from('page_views')
        .update({
          scroll_depth_percentage: percentage,
          max_scroll_depth: Math.max(percentage, 0)
        })
        .eq('id', this.currentPageViewId);
    } catch (error) {
      console.error('Error updating scroll depth:', error);
    }
  }

  private async getSessionDatabaseId(): Promise<string | null> {
    if (!this.sessionId) return null;

    try {
      const { data } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('session_id', this.sessionId)
        .single();

      return data?.id || null;
    } catch (error) {
      console.error('Error getting session ID:', error);
      return null;
    }
  }

  async endSession() {
    if (!this.sessionId) return;

    if (this.currentPageViewId) {
      await this.endPageView(this.currentPageViewId);
    }

    try {
      await supabase
        .from('user_sessions')
        .update({
          ended_at: new Date().toISOString()
        })
        .eq('session_id', this.sessionId);
    } catch (error) {
      console.error('Error ending session:', error);
    }

    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
    }
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getVisitorId(): string | null {
    return this.visitorId;
  }

  getCurrentPageViewId(): string | null {
    return this.currentPageViewId;
  }
}
